import { Box, styled } from "@mui/system";
import React, { FC, useState } from "react";
import muiIcons from "../icons/mui-icons";
import { H5, Paragraph } from "../Typography";
import useCommonContext from "../../hooks/useCommonContext";
import MuiAccordion, { AccordionProps } from "@mui/material/Accordion";
import { firstTableData, secondTableData } from "./payment-data";
import {
  AccordionDetails,
  AccordionSummary,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@mui/material";

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters {...props} />
))(({ theme }) => ({
  borderBottom: `1px dashed ${theme.palette.grey[300]}`,
  "&:last-child": {
    borderBottom: 0,
  },
  "&:before": {
    display: "none",
  },
}));

const AccordionHeader = styled(Paper)(() => ({
  display: "flex",
  paddingTop: "1rem",
  paddingBottom: "1rem",
  alignItems: "center",
  justifyContent: "space-between",
}));

const AccordionBody = styled(AccordionDetails)(({ theme }) => ({
  width: "100%",
  display: "flex",
  justifyContent: "space-between",
  [theme.breakpoints.down("md")]: {
    display: "block",
  },
}));

const CardImg = styled("img")(() => ({
  width: "35px",
  marginLeft: "15px",
  marginRight: "15px",
}));

const StyledTable = styled(Table)(() => ({
  "& td": {
    border: "none",
    padding: ".3rem .5rem",
    paddingRight: "3rem",
  },
  "& #firstCell": {
    fontWeight: 600,
    color: "#9e9e9e",
    paddingRight: "1rem",
    width: "120px",
  },
}));

interface Props {
  expanded: any;
  panel: string;
}
function DropdownIcon(props: Props) {
  const { expanded, panel } = props;
  const { DownArrowIcon, RightArrowIcon } = muiIcons;
  return (
    <IconButton color="primary">
      {expanded === panel ? (
        <DownArrowIcon sx={{ fontSize: 12 }} />
      ) : (
        <RightArrowIcon sx={{ fontSize: 12 }} />
      )}
    </IconButton>
  );
}

const PaymentMethods: FC = () => {
  const { EditIcon } = muiIcons;
  const iconLink = "/assets/images/payment-cards";
  const { setPaymentMethodModal } = useCommonContext();

  const [expanded, setExpanded] = useState<string | false>("panel1");
  const handleAccordion =
    (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
      setExpanded(newExpanded ? panel : false);
    };

  return (
    <>
      <Accordion
        expanded={expanded === "panel1"}
        onChange={handleAccordion("panel1")}
      >
        <AccordionHeader>
          <AccordionSummary sx={{ p: 0 }}>
            <Box display="flex" alignItems="center">
              <DropdownIcon panel="panel1" expanded={expanded} />
              <CardImg src={`${iconLink}/022-paypal.svg`} alt="" />
              <Box>
                <H5>Paypal Card</H5>
                <Paragraph sx={{ fontSize: 13 }}>Expires Dec 2022</Paragraph>
              </Box>
            </Box>
          </AccordionSummary>
          <IconButton
            color="primary"
            onClick={() => setPaymentMethodModal(true)}
          >
            <EditIcon />
          </IconButton>
        </AccordionHeader>

        <AccordionBody>
          <Box>
            <StyledTable>
              <TableBody>
                {firstTableData.map((data) => (
                  <TableRow tabIndex={-1}>
                    <TableCell align="left" id="firstCell">
                      {data.title}
                    </TableCell>
                    <TableCell align="left">{data.value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </StyledTable>
          </Box>

          <Box>
            <StyledTable>
              <TableBody>
                {secondTableData.map((data) => (
                  <TableRow tabIndex={-1}>
                    <TableCell align="left" id="firstCell">
                      {data.title}
                    </TableCell>
                    <TableCell align="left">{data.value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </StyledTable>
          </Box>
        </AccordionBody>
      </Accordion>

      <Accordion
        expanded={expanded === "panel2"}
        onChange={handleAccordion("panel2")}
      >
        <AccordionHeader>
          <AccordionSummary sx={{ p: 0 }}>
            <Box display="flex" alignItems="center">
              <DropdownIcon panel="panel2" expanded={expanded} />
              <CardImg src={`${iconLink}/011-visa.svg`} alt="" />
              <Box>
                <H5>Visa Card</H5>
                <Paragraph sx={{ fontSize: 13 }}>Expires Feb 2022</Paragraph>
              </Box>
            </Box>
          </AccordionSummary>
          <IconButton
            color="primary"
            onClick={() => setPaymentMethodModal(true)}
          >
            <EditIcon />
          </IconButton>
        </AccordionHeader>
        <AccordionBody>
          <Box>
            <StyledTable>
              <TableBody>
                {firstTableData.map((data) => (
                  <TableRow tabIndex={-1}>
                    <TableCell align="left" id="firstCell">
                      {data.title}
                    </TableCell>
                    <TableCell align="left">{data.value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </StyledTable>
          </Box>

          <Box>
            <StyledTable>
              <TableBody>
                {secondTableData.map((data) => (
                  <TableRow tabIndex={-1}>
                    <TableCell align="left" id="firstCell">
                      {data.title}
                    </TableCell>
                    <TableCell align="left">{data.value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </StyledTable>
          </Box>
        </AccordionBody>
      </Accordion>

      <Accordion
        expanded={expanded === "panel3"}
        onChange={handleAccordion("panel3")}
      >
        <AccordionHeader>
          <AccordionSummary sx={{ p: 0 }}>
            <Box display="flex" alignItems="center">
              <DropdownIcon panel="panel3" expanded={expanded} />
              <CardImg src={`${iconLink}/007-american express.svg`} alt="" />
              <Box>
                <H5>American Express</H5>
                <Paragraph sx={{ fontSize: 13 }}>Expires Dec 2022</Paragraph>
              </Box>
            </Box>
          </AccordionSummary>
          <IconButton
            color="primary"
            onClick={() => setPaymentMethodModal(true)}
          >
            <EditIcon />
          </IconButton>
        </AccordionHeader>

        <AccordionBody>
          <Box>
            <StyledTable>
              <TableBody>
                {firstTableData.map((data) => (
                  <TableRow tabIndex={-1}>
                    <TableCell align="left" id="firstCell">
                      {data.title}
                    </TableCell>
                    <TableCell align="left">{data.value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </StyledTable>
          </Box>

          <Box>
            <StyledTable>
              <TableBody>
                {secondTableData.map((data) => (
                  <TableRow tabIndex={-1}>
                    <TableCell
                      align="left"
                      sx={{ fontWeight: 600, color: "#9e9e9e", width: "200px" }}
                    >
                      id="firstCell" {data.title}
                    </TableCell>
                    <TableCell align="left">{data.value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </StyledTable>
          </Box>
        </AccordionBody>
      </Accordion>
    </>
  );
};

export default PaymentMethods;
